import * as React from "react";
import { useEffect, useState } from "react";
import axios from "axios";
import { Button, CircularProgress, Paper, Tab, TableCell, TableRow, Tabs } from "@material-ui/core";
import GetAppIcon from "@material-ui/icons/GetApp";
import { Notification, useNotify } from "react-admin";
import { ENDPOINTS } from "../apiConstants";
import { getConfigGet } from "../getConfig";
import { BrandPicker, brandLabel, useBrandNames } from "./brands";
import { downloadCsv } from "./csv";
import {
  DataTable,
  PageHeader,
  StatCard,
  StatusChip,
  errorMessage,
  fmtDate,
  fmtPeriod,
  gbp,
  useCloStyles,
} from "./shared";

const BUCKETS = [
  { key: "current", label: "Current" },
  { key: "1_30", label: "1–30" },
  { key: "31_60", label: "31–60" },
  { key: "61_90", label: "61–90" },
  { key: "90_plus", label: "90+" },
];

const STATEMENT_COLUMNS = [
  { label: "Period" },
  { label: "Due" },
  { label: "Status" },
  { label: "Invoiced", numeric: true },
  { label: "Paid", numeric: true },
  { label: "Outstanding", numeric: true },
];

const AGED_COLUMNS = [
  { label: "Brand" },
  ...BUCKETS.map((b) => ({ label: b.label, numeric: true })),
  { label: "Total", numeric: true },
];

const pounds = (minor: number | null | undefined): string => (Number(minor || 0) / 100).toFixed(2);

const today = () => new Date().toISOString().slice(0, 10);

const Statement = () => {
  const classes = useCloStyles();
  const notify = useNotify();
  const [brandId, setBrandId] = useState("");
  const [loading, setLoading] = useState(false);
  const [statement, setStatement] = useState<any>(null);
  const names = useBrandNames([brandId, statement?.brandId]);

  const run = async () => {
    if (!brandId) {
      notify("Pick a brand", "warning");
      return;
    }
    try {
      setLoading(true);
      const res: any = await axios.get(
        ENDPOINTS.CLO_BILLING_REPORTS,
        getConfigGet({ report: "statement", brand_id: brandId })
      );
      setStatement(res.data);
    } catch (err) {
      setStatement(null);
      notify(errorMessage(err, "Failed to load statement"), "warning");
    } finally {
      setLoading(false);
    }
  };

  const months: any[] = statement?.months || [];

  const exportCsv = () => {
    downloadCsv(
      `clo-statement-${statement.brandId}-${today()}.csv`,
      ["period", "due_on", "status", "overdue", "invoiced_gbp", "paid_gbp", "outstanding_gbp"],
      months.map((m) => ({
        period: m.period,
        due_on: m.dueOn,
        status: m.status,
        overdue: m.overdue ? "yes" : "no",
        invoiced_gbp: pounds(m.invoicedMinor),
        paid_gbp: pounds(m.paidMinor),
        outstanding_gbp: pounds(m.outstandingMinor),
      }))
    );
  };

  return (
    <>
      <div className={classes.filterContainer}>
        <BrandPicker className={classes.filterField} value={brandId} onChange={setBrandId} required />
        <Button className={classes.filterButton} variant="contained" color="primary" disableElevation onClick={run} disabled={loading}>
          Run
        </Button>
        {statement && (
          <Button
            className={classes.filterButton}
            variant="outlined"
            color="primary"
            startIcon={<GetAppIcon />}
            onClick={exportCsv}
            disabled={months.length === 0}
          >
            CSV
          </Button>
        )}
      </div>

      {loading && <div className={classes.loadingContainer}><CircularProgress size={40} /></div>}

      {!loading && statement && (
        <>
          <div className={classes.statRow}>
            <StatCard label="Invoiced" value={gbp(statement.invoicedMinor)} />
            <StatCard label="Paid" value={gbp(statement.paidMinor)} />
            <StatCard label="Outstanding" value={gbp(statement.outstandingMinor)} />
            <StatCard label="Overdue" value={gbp(statement.overdueMinor)} />
            <StatCard label="Unallocated credit" value={gbp(statement.creditMinor)} />
          </div>
          <DataTable
            columns={STATEMENT_COLUMNS}
            isEmpty={months.length === 0}
            empty={`No invoices for ${brandLabel(names, statement.brandId)} yet.`}
          >
            {months.map((m) => (
              <TableRow key={m.period} hover className={m.overdue ? classes.warnRow : undefined}>
                <TableCell>{fmtPeriod(m.period)}</TableCell>
                <TableCell>
                  {fmtDate(m.dueOn)}
                  {m.overdue && <span className={classes.muted}> · overdue</span>}
                </TableCell>
                <TableCell>{m.status ? <StatusChip status={m.status} /> : "—"}</TableCell>
                <TableCell align="right">{gbp(m.invoicedMinor)}</TableCell>
                <TableCell align="right">{gbp(m.paidMinor)}</TableCell>
                <TableCell align="right"><strong>{gbp(m.outstandingMinor)}</strong></TableCell>
              </TableRow>
            ))}
          </DataTable>
        </>
      )}
    </>
  );
};

const AgedDebt = () => {
  const classes = useCloStyles();
  const notify = useNotify();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<any[]>([]);
  const names = useBrandNames(rows.map((r) => r.brandId));

  const load = async () => {
    try {
      setLoading(true);
      const res: any = await axios.get(ENDPOINTS.CLO_BILLING_REPORTS, getConfigGet({ report: "aged_debt" }));
      setRows(Array.isArray(res.data) ? res.data : res.data?.data || []);
    } catch (err) {
      notify(errorMessage(err, "Failed to load aged debt"), "warning");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const bucketTotal = (key: string) => rows.reduce((sum, r) => sum + Number(r.buckets?.[key] || 0), 0);
  const grandTotal = rows.reduce((sum, r) => sum + Number(r.totalMinor || 0), 0);

  const exportCsv = () => {
    const cols = ["brand_id", "brand", ...BUCKETS.map((b) => `${b.key}_gbp`), "total_gbp"];
    const data = rows.map((r) => {
      const row: Record<string, any> = { brand_id: r.brandId, brand: names[String(r.brandId)] || "" };
      BUCKETS.forEach((b) => {
        row[`${b.key}_gbp`] = pounds(r.buckets?.[b.key]);
      });
      row.total_gbp = pounds(r.totalMinor);
      return row;
    });
    downloadCsv(`clo-aged-debt-${today()}.csv`, cols, data);
  };

  return (
    <>
      <div className={classes.filterContainer}>
        <Button className={classes.filterButton} variant="contained" color="primary" disableElevation onClick={load} disabled={loading}>
          Refresh
        </Button>
        <Button
          className={classes.filterButton}
          variant="outlined"
          color="primary"
          startIcon={<GetAppIcon />}
          onClick={exportCsv}
          disabled={loading || rows.length === 0}
        >
          CSV
        </Button>
      </div>

      {loading ? (
        <div className={classes.loadingContainer}><CircularProgress size={40} /></div>
      ) : (
        <DataTable columns={AGED_COLUMNS} isEmpty={rows.length === 0} empty="No outstanding retailer debt.">
          {rows.map((r) => (
            <TableRow key={r.brandId} hover>
              <TableCell>{brandLabel(names, r.brandId)}</TableCell>
              {BUCKETS.map((b) => (
                <TableCell key={b.key} align="right">{gbp(r.buckets?.[b.key] || 0)}</TableCell>
              ))}
              <TableCell align="right"><strong>{gbp(r.totalMinor)}</strong></TableCell>
            </TableRow>
          ))}
          <TableRow className={classes.totalRow}>
            <TableCell>Total</TableCell>
            {BUCKETS.map((b) => (
              <TableCell key={b.key} align="right">{gbp(bucketTotal(b.key))}</TableCell>
            ))}
            <TableCell align="right">{gbp(grandTotal)}</TableCell>
          </TableRow>
        </DataTable>
      )}
    </>
  );
};

const Reports = () => {
  const classes = useCloStyles();
  const [tab, setTab] = useState(0);

  return (
    <div className={classes.main}>
      <PageHeader title="Reports" subtitle="Retailer statements and aged debt across all retailers" />
      <Paper className={classes.tableContainer} style={{ marginBottom: 16 }}>
        <Tabs value={tab} onChange={(_e, v) => setTab(v)} indicatorColor="primary" textColor="primary">
          <Tab label="Statement" />
          <Tab label="Aged debt" />
        </Tabs>
      </Paper>
      <div hidden={tab !== 0}><Statement /></div>
      <div hidden={tab !== 1}><AgedDebt /></div>
      <Notification />
    </div>
  );
};

export default Reports;
